<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { IntegrationState, type JiraIntegrationStatus, type JiraSite } from '@/api';
import { setIntegrationOauthContext } from '@/services/integration/integrationOauthService';
import {
  fetchJiraIntegrationStatus,
  fetchJiraSites,
  removeJiraIntegration,
  requestJiraAuthorization,
  selectJiraSiteForOrganisation,
} from '@/services/integration/jiraIntegrationService';
import { useAuthStore } from '@/stores/auth';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import GaiaBanner from '../../composite/GaiaBanner.vue';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaEmptyState from '../../composite/GaiaEmptyState.vue';
import GaiaModal from '../../base/GaiaModal.vue';
import GaiaPickerOption from '../../composite/GaiaPickerOption.vue';
import GaiaPopconfirm from '../../composite/GaiaPopconfirm.vue';
import GaiaSpinner from '../../base/GaiaSpinner.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaAvatar from '../../base/GaiaAvatar.vue';
import type { GaiaChipTone } from '../../base/GaiaStatusChip.vue';
import GaiaIntegrationCard from './GaiaIntegrationCard.vue';

const message = useGaiaToast();
const authStore = useAuthStore();

const status = ref<JiraIntegrationStatus | null>(null);
const loading = ref(false);
const actionLoading = ref(false);

const showSiteModal = ref(false);
const sites = ref<JiraSite[]>([]);
const sitesLoading = ref(false);
const sitesError = ref('');
const selectedCloudId = ref<string | null>(null);

const jiraState = computed<IntegrationState>(
  () => status.value?.state ?? IntegrationState.DISCONNECTED
);
const isConnected = computed(() => jiraState.value === IntegrationState.CONNECTED);
// `pending` means the consent is stored but no site is committed yet, so the integration is not
// usable. Offering "finish setup" is the right move here, not a fresh consent.
const isPending = computed(() => jiraState.value === IntegrationState.PENDING);

const stateTone = computed<GaiaChipTone>(() => {
  if (isConnected.value) return 'success';
  if (isPending.value) return 'warning';
  return 'neutral';
});

const stateLabel = computed(() => {
  if (isConnected.value) return 'Connected';
  if (isPending.value) return 'Finish setup';
  return 'Not Connected';
});

const helperMessage = computed(() => {
  if (isConnected.value) {
    return 'Jira is connected. Intake agents can receive issue attachments from this site.';
  }
  if (isPending.value) {
    return 'Consent granted. Choose which Jira site this organisation should use.';
  }
  return 'Connect Jira: you will be redirected to Atlassian to approve the Gaia app.';
});

const authorizedAt = computed(() =>
  status.value?.authorized_at ? new Date(status.value.authorized_at).toLocaleString() : ''
);

const isOrgAdmin = computed(() => authStore.userPermissions.is_org_admin);

async function refresh(): Promise<void> {
  loading.value = true;
  try {
    status.value = await fetchJiraIntegrationStatus();
  } catch (error) {
    logger.error('Failed to fetch Jira status:', error);
    message.error(extractErrorDetail(error, 'Failed to fetch Jira status.'));
    status.value = null;
  } finally {
    loading.value = false;
  }
}

async function loadSites(): Promise<void> {
  sitesLoading.value = true;
  sitesError.value = '';
  try {
    sites.value = await fetchJiraSites();
    // Pre-select the committed site so "switch site" opens on the current choice.
    const committed = sites.value.find(site => site.cloud_id === status.value?.cloud_id);
    const fallback = sites.value.length > 0 ? sites.value[0].cloud_id : null;
    selectedCloudId.value = committed?.cloud_id ?? fallback;
  } catch (error) {
    logger.error('Failed to list Jira sites:', error);
    sites.value = [];
    selectedCloudId.value = null;
    // The list is fetched live from Atlassian, so a dead grant surfaces here rather than on
    // /status — show it in place with a retry instead of an empty list.
    sitesError.value = extractErrorDetail(error, 'Could not list the accessible Jira sites.');
  } finally {
    sitesLoading.value = false;
  }
}

function openSiteModal(): void {
  if (!isOrgAdmin.value) {
    message.error('You do not have permission to manage integrations.');
    return;
  }

  showSiteModal.value = true;
  void loadSites();
}

async function handleConfirmSite(): Promise<void> {
  const cloudId = selectedCloudId.value;
  if (!cloudId) {
    message.error('Please choose a Jira site first.');
    return;
  }

  actionLoading.value = true;
  try {
    status.value = await selectJiraSiteForOrganisation(cloudId);
    showSiteModal.value = false;
    message.success('Jira site selected.');
  } catch (error) {
    logger.error('Failed to select Jira site:', error);
    message.error(extractErrorDetail(error, 'Failed to select the Jira site.'));
    // A rejected cloud_id means the list is stale (the authorization no longer covers it).
    await loadSites();
  } finally {
    actionLoading.value = false;
  }
}

async function handleConnect(): Promise<void> {
  if (!isOrgAdmin.value) {
    message.error('You do not have permission to manage integrations.');
    return;
  }

  actionLoading.value = true;
  try {
    const { url, state } = await requestJiraAuthorization();
    setIntegrationOauthContext({
      provider: 'jira',
      origin: 'integrations_org',
      scope: 'organisation',
      state,
    });
    window.location.href = url;
  } catch (error) {
    logger.error('Failed to start Jira OAuth flow:', error);
    message.error(extractErrorDetail(error, 'Could not start Jira connection.'));
  } finally {
    actionLoading.value = false;
  }
}

async function handleDisconnect(): Promise<void> {
  if (!isOrgAdmin.value) {
    message.error('You do not have permission to manage integrations.');
    return;
  }

  actionLoading.value = true;
  try {
    status.value = await removeJiraIntegration();
    sites.value = [];
    selectedCloudId.value = null;
    message.success('Jira disconnected.');
  } catch (error) {
    logger.error('Failed to disconnect Jira:', error);
    message.error(extractErrorDetail(error, 'Failed to disconnect Jira.'));
  } finally {
    actionLoading.value = false;
  }
}

onMounted(refresh);

defineExpose({ refresh, openSiteModal, loading });
</script>

<template>
  <gaia-integration-card
    title="Jira"
    :tone="stateTone"
    :status-label="stateLabel"
    :message="helperMessage"
    :loading="loading"
  >
    <template v-if="isConnected" #meta>
      <gaia-text v-if="status?.site_name" size="micro" tone="muted">
        Site: {{ status.site_name }}
      </gaia-text>
      <gaia-text v-if="status?.site_url" size="micro" tone="muted">{{ status.site_url }}</gaia-text>
      <gaia-text v-if="status?.account_display_name" size="micro" tone="muted">
        Authorized by: {{ status.account_display_name }}
      </gaia-text>
      <gaia-text v-if="authorizedAt" size="micro" tone="muted">
        Authorized at: {{ authorizedAt }}
      </gaia-text>
    </template>

    <template #actions>
      <gaia-button
        v-if="!isConnected && !isPending"
        type="primary"
        size="small"
        :loading="actionLoading"
        :disabled="!isOrgAdmin"
        @click="handleConnect"
      >
        Connect
      </gaia-button>
      <gaia-button
        v-if="isPending"
        type="primary"
        size="small"
        :loading="actionLoading"
        :disabled="!isOrgAdmin"
        @click="openSiteModal"
      >
        Choose site
      </gaia-button>
      <gaia-button
        v-if="isConnected"
        size="small"
        :loading="actionLoading"
        :disabled="!isOrgAdmin"
        @click="openSiteModal"
      >
        Switch site
      </gaia-button>
      <!-- No "Reconnect" here on purpose: POST /connect rejects a code while the integration is
           still connected, so re-consenting has to start from Disconnect. -->
      <gaia-popconfirm
        v-if="isConnected || isPending"
        title="Disconnect Jira?"
        confirm-label="Disconnect"
        :width="320"
        @confirm="handleDisconnect"
      >
        <template #trigger>
          <gaia-button size="small" tertiary type="error" :disabled="!isOrgAdmin">
            Disconnect
          </gaia-button>
        </template>
        Intake agents keep their webhook URLs, but Jira events will be dropped until Jira is
        reconnected. Reconnecting starts again at the Atlassian consent screen.
      </gaia-popconfirm>
    </template>
  </gaia-integration-card>

  <gaia-modal
    v-model:show="showSiteModal"
    title="Choose a Jira site"
    width="480px"
    content-style="padding: 16px 20px"
  >
    <gaia-spinner :show="sitesLoading">
      <n-flex vertical :size="12" :wrap="false">
        <template v-if="sitesError">
          <gaia-banner plain type="error">{{ sitesError }}</gaia-banner>
          <gaia-button size="small" :loading="sitesLoading" @click="loadSites">Retry</gaia-button>
        </template>

        <gaia-empty-state
          v-else-if="!sitesLoading && sites.length === 0"
          dashed
          title="No Jira sites available"
        >
          The authorization does not cover any Jira sites.
        </gaia-empty-state>

        <n-flex v-else vertical :size="0" :wrap="false">
          <gaia-picker-option
            v-for="site in sites"
            :key="site.cloud_id"
            radio
            :label="site.name"
            :meta="site.url"
            :selected="selectedCloudId === site.cloud_id"
            @select="selectedCloudId = site.cloud_id"
          >
            <template #leading>
              <gaia-avatar
                v-if="site.avatar_url"
                variant="mark"
                :size="26"
                :src="site.avatar_url"
              />
            </template>
          </gaia-picker-option>
        </n-flex>
      </n-flex>
    </gaia-spinner>

    <template #footer>
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button size="small" @click="showSiteModal = false">Cancel</gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :loading="actionLoading"
          :disabled="!selectedCloudId"
          @click="handleConfirmSite"
        >
          Use this site
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
